import type { tPrompt } from '../../types';
import type { PMPartsAndProcedure } from '../../types/pmTypes';
import { createServiceItem, updateServiceItem } from '../../graphql/companyResourceQueries';

/** Required `itemName` for a category-only Resources row until a real item is added. */
export const CATEGORY_STUB_ITEM_NAME = '__category_placeholder__';

export type ServiceCatalogActor = {
    companyId: string;
    createdByUserId: string;
    createdByUserName: string;
};

export function isServiceItemPlaceholder(item: Pick<PMPartsAndProcedure, 'itemName'>): boolean {
    const name = item.itemName?.trim();
    return !name || name === CATEGORY_STUB_ITEM_NAME;
}

export function uniqueServiceItemCategories(items: PMPartsAndProcedure[], extra: string[] = []): string[] {
    const names = new Map<string, string>();
    for (const item of items) {
        const category = item.category?.trim();
        if (!category) continue;
        const key = category.toLowerCase();
        if (!names.has(key)) names.set(key, category);
    }
    for (const extraName of extra) {
        const category = extraName.trim();
        if (!category) continue;
        const key = category.toLowerCase();
        if (!names.has(key)) names.set(key, category);
    }
    return [...names.values()].sort((a, b) => a.localeCompare(b));
}

export const DS_SECTION_CATALOG = {
    SERVICE_ITEMS: 'serviceItems',
    PROCEDURES: 'procedures',
} as const;

export type DsSectionCatalogKind = (typeof DS_SECTION_CATALOG)[keyof typeof DS_SECTION_CATALOG];

export function isDsSectionCatalogKind(value: string | null | undefined): value is DsSectionCatalogKind {
    return value === DS_SECTION_CATALOG.SERVICE_ITEMS || value === DS_SECTION_CATALOG.PROCEDURES;
}

type ProcedureCategorySource = { category?: string | null };

export function headingNamesForCatalog(
    kind: DsSectionCatalogKind,
    items: PMPartsAndProcedure[],
    procedures: ProcedureCategorySource[],
    extra: string[] = [],
): string[] {
    if (kind === DS_SECTION_CATALOG.PROCEDURES) {
        const procedureCategories = procedures
            .map((procedure) => procedure.category?.trim() || '')
            .filter(Boolean);
        return uniqueServiceItemCategories([], [...extra, ...procedureCategories]);
    }
    return uniqueServiceItemCategories(items, extra);
}

/** DS section headings can come from Service Items or Procedure categories. */
export function uniqueSectionHeadingNames(
    items: PMPartsAndProcedure[],
    procedures: ProcedureCategorySource[],
    extra: string[] = [],
): string[] {
    return uniqueServiceItemCategories(items, [
        ...extra,
        ...procedures.map((procedure) => procedure.category?.trim() || '').filter(Boolean),
    ]);
}

export function findProcedureCategoryCasing(
    procedures: ProcedureCategorySource[],
    name: string,
): string | undefined {
    const lower = name.trim().toLowerCase();
    if (!lower) return undefined;
    return procedures.find((procedure) => (procedure.category || '').trim().toLowerCase() === lower)?.category?.trim();
}

/**
 * New free-text headings still get a Service Items stub so consumables can attach.
 * Procedure-only headings (e.g. General, Lubrication) must not require a stub.
 */
export function shouldStubServiceItemCategory(
    name: string,
    items: PMPartsAndProcedure[],
    procedures: ProcedureCategorySource[],
    catalogKind?: DsSectionCatalogKind | null,
): boolean {
    if (catalogKind === DS_SECTION_CATALOG.PROCEDURES) return false;
    const trimmed = name.trim();
    if (!trimmed) return false;
    if (findCategoryCasing(items, trimmed)) return false;
    if (findProcedureCategoryCasing(procedures, trimmed)) return false;
    return true;
}

export function findCategoryCasing(items: PMPartsAndProcedure[], name: string): string | undefined {
    const lower = name.trim().toLowerCase();
    if (!lower) return undefined;
    return items.find((item) => (item.category || '').trim().toLowerCase() === lower)?.category?.trim();
}

export function itemsInCategory(items: PMPartsAndProcedure[], categoryName: string): PMPartsAndProcedure[] {
    const lower = categoryName.trim().toLowerCase();
    return items.filter(
        (item) => (item.category || '').trim().toLowerCase() === lower && !isServiceItemPlaceholder(item),
    );
}

export function findCategoryStub(
    items: PMPartsAndProcedure[],
    categoryName: string,
): PMPartsAndProcedure | undefined {
    const lower = categoryName.trim().toLowerCase();
    return items.find(
        (item) => (item.category || '').trim().toLowerCase() === lower && isServiceItemPlaceholder(item),
    );
}

function asCatalogItem(data: unknown): PMPartsAndProcedure | null {
    if (!data || typeof data !== 'object' || !('id' in data) || typeof (data as { id?: unknown }).id !== 'string') {
        return null;
    }
    return data as PMPartsAndProcedure;
}

export async function createCategoryStub(
    categoryName: string,
    actor: ServiceCatalogActor,
): Promise<PMPartsAndProcedure | null> {
    const created = await createServiceItem({
        companyId: actor.companyId,
        itemName: CATEGORY_STUB_ITEM_NAME,
        category: categoryName,
        itemType: 'Consumable',
        createdByUserId: actor.createdByUserId,
        createdByUserName: actor.createdByUserName,
    });
    return asCatalogItem(created);
}

export type CatalogItemFields = {
    partNumber?: string | null;
    manufacturer?: string | null;
    vendorId?: string | null;
    vendorName?: string | null;
    unitOfMeasure?: string | null;
};

type CatalogVendor = { id?: string | null; name?: string | null };

type ListingKey = Pick<CatalogItemFields, 'vendorId' | 'vendorName' | 'manufacturer' | 'partNumber'> & {
    itemName: string;
};

function catalogPartNumberValue(partNumber?: string | null): string | null {
    return partNumber?.trim() || null;
}

function catalogTextValue(value?: string | null): string | null {
    return value?.trim() || null;
}

function normKey(value?: string | null): string {
    return (value || '').trim().toLowerCase();
}

function vendorIdentityKey(item?: Pick<CatalogItemFields, 'vendorId' | 'vendorName'> | null): string {
    const vendorId = item?.vendorId?.trim();
    if (vendorId) return `id:${vendorId}`;
    const vendorName = item?.vendorName?.trim();
    if (vendorName) return `name:${vendorName.toLowerCase()}`;
    return '';
}

export function catalogHasVendor(
    item?: Pick<PMPartsAndProcedure, 'vendorId' | 'vendorName'> | CatalogItemFields | null,
): boolean {
    return Boolean(item?.vendorId?.trim() || item?.vendorName?.trim());
}

export function vendorLabelForCatalogItem(
    item: Pick<PMPartsAndProcedure, 'vendorId' | 'vendorName'>,
    vendors: CatalogVendor[] = [],
): string {
    const byId = item.vendorId ? vendors.find((vendor) => vendor.id === item.vendorId) : undefined;
    return (byId?.name || item.vendorName || '').trim();
}

export function resolveVendorFromName(
    vendors: CatalogVendor[],
    vendorName?: string,
): Pick<CatalogItemFields, 'vendorId' | 'vendorName'> {
    const trimmed = vendorName?.trim() || '';
    if (!trimmed) return { vendorId: null, vendorName: null };
    const match = vendors.find((vendor) => (vendor.name || '').trim().toLowerCase() === trimmed.toLowerCase());
    return {
        vendorId: match?.id || null,
        vendorName: match?.name?.trim() || trimmed,
    };
}

export function catalogFieldsFromPrompt(
    prompt: Pick<tPrompt, 'partNumber' | 'vendor' | 'partDescription' | 'defaultUM'>,
    vendors: CatalogVendor[] = [],
    overrides?: { partNumber?: string; vendorName?: string; manufacturer?: string; unitOfMeasure?: string },
): CatalogItemFields {
    return {
        partNumber: overrides?.partNumber ?? prompt.partNumber,
        manufacturer: overrides?.manufacturer ?? prompt.partDescription,
        unitOfMeasure: overrides?.unitOfMeasure ?? prompt.defaultUM,
        ...resolveVendorFromName(vendors, overrides?.vendorName ?? prompt.vendor),
    };
}

/** Fields typed on a new Daily Service row that the catalog create did not store yet. */
export function pendingListingOverrides(
    prompt: Pick<tPrompt, 'partNumber' | 'partDescription' | 'vendor' | 'defaultUM'>,
    item: Pick<PMPartsAndProcedure, 'partNumber' | 'manufacturer' | 'vendorName' | 'unitOfMeasure'>,
): { partNumber?: string; manufacturer?: string; vendorName?: string; unitOfMeasure?: string } | null {
    const overrides: { partNumber?: string; manufacturer?: string; vendorName?: string; unitOfMeasure?: string } = {};
    const partNumber = prompt.partNumber?.trim();
    if (partNumber && partNumber !== (item.partNumber || '').trim()) overrides.partNumber = partNumber;
    const manufacturer = prompt.partDescription?.trim();
    if (manufacturer && manufacturer !== (item.manufacturer || '').trim()) overrides.manufacturer = manufacturer;
    const unitOfMeasure = prompt.defaultUM?.trim();
    if (unitOfMeasure && unitOfMeasure !== (item.unitOfMeasure || '').trim()) overrides.unitOfMeasure = unitOfMeasure;
    const vendor = prompt.vendor?.trim();
    if (vendor && vendor.toLowerCase() !== (item.vendorName || '').trim().toLowerCase()) overrides.vendorName = vendor;
    return Object.keys(overrides).length ? overrides : null;
}

function vendorWriteFields(
    fields?: Pick<PMPartsAndProcedure, 'vendorId' | 'vendorName'> | CatalogItemFields | null,
): { vendorId: string | null; vendorName: string | null } | null {
    if (!catalogHasVendor(fields)) return null;
    return {
        vendorId: fields?.vendorId?.trim() || null,
        vendorName: fields?.vendorName?.trim() || null,
    };
}

export function listingsAreSameVariant(a: ListingKey, b: ListingKey): boolean {
    return (
        normKey(a.itemName) === normKey(b.itemName) &&
        vendorIdentityKey(a) === vendorIdentityKey(b) &&
        normKey(a.manufacturer) === normKey(b.manufacturer) &&
        normKey(a.partNumber) === normKey(b.partNumber)
    );
}

/**
 * Same part number, manufacturer, and vendor. A different vendor may reuse the part number.
 * Blank part numbers are not treated as duplicates.
 */
export function findPartVendorConflict(
    items: PMPartsAndProcedure[],
    key: Pick<ListingKey, 'partNumber' | 'manufacturer' | 'vendorId' | 'vendorName'>,
    excludeId?: string,
): PMPartsAndProcedure | undefined {
    const partNumber = normKey(key.partNumber);
    if (!partNumber) return undefined;
    const manufacturer = normKey(key.manufacturer);
    const vendor = vendorIdentityKey(key);
    return items.find((item) => {
        if (excludeId && item.id === excludeId) return false;
        if ((item.itemType || '').trim().toLowerCase() === 'procedure') return false;
        return (
            normKey(item.partNumber) === partNumber &&
            normKey(item.manufacturer) === manufacturer &&
            vendorIdentityKey(item) === vendor
        );
    });
}

export function findMatchingListing(
    items: PMPartsAndProcedure[],
    categoryName: string,
    key: ListingKey,
    excludeId?: string,
): PMPartsAndProcedure | undefined {
    return itemsInCategory(items, categoryName).find(
        (item) => (!excludeId || item.id !== excludeId) && listingsAreSameVariant(item, key),
    );
}

export function serviceItemOptionLabel(
    item: PMPartsAndProcedure,
    vendors: CatalogVendor[] = [],
    siblings: PMPartsAndProcedure[] = [],
): string {
    const name = item.itemName.trim();
    const sameNameCount = siblings.filter(
        (sibling) => sibling.itemName.trim().toLowerCase() === name.toLowerCase(),
    ).length;
    if (sameNameCount <= 1) return name;

    const vendor = vendorLabelForCatalogItem(item, vendors);
    const manufacturer = (item.manufacturer || '').trim();
    const partNumber = (item.partNumber || '').trim();
    const details = [
        manufacturer && manufacturer.toLowerCase() !== vendor.toLowerCase() ? manufacturer : '',
        partNumber,
        vendor,
    ].filter(Boolean);
    return details.length ? `${name} · ${details.join(' · ')}` : name;
}

function canFillListingInPlace(existing: PMPartsAndProcedure, fields?: CatalogItemFields): boolean {
    const nextPart = catalogPartNumberValue(fields?.partNumber);
    const nextMfr = catalogTextValue(fields?.manufacturer);
    const nextVendor = vendorWriteFields(fields);
    const partOk = !nextPart || !(existing.partNumber || '').trim() || normKey(existing.partNumber) === normKey(nextPart);
    const mfrOk =
        !nextMfr || !(existing.manufacturer || '').trim() || normKey(existing.manufacturer) === normKey(nextMfr);
    const vendorOk = !nextVendor || !catalogHasVendor(existing) || vendorIdentityKey(existing) === vendorIdentityKey(nextVendor);
    return partOk && mfrOk && vendorOk;
}

function listingWritePatch(existing: PMPartsAndProcedure, fields?: CatalogItemFields) {
    const nextPart = catalogPartNumberValue(fields?.partNumber);
    const nextMfr = catalogTextValue(fields?.manufacturer);
    const nextUm = catalogTextValue(fields?.unitOfMeasure);
    const nextVendor = vendorWriteFields(fields);
    const patch: Record<string, string | null> = {};
    if (nextPart && !(existing.partNumber || '').trim()) patch.partNumber = nextPart;
    if (nextMfr && !(existing.manufacturer || '').trim()) patch.manufacturer = nextMfr;
    if (nextUm && !(existing.unitOfMeasure || '').trim()) patch.unitOfMeasure = nextUm;
    if (nextVendor && !catalogHasVendor(existing)) {
        patch.vendorId = nextVendor.vendorId;
        patch.vendorName = nextVendor.vendorName;
    }
    return patch;
}

async function createListingRow(
    categoryName: string,
    itemName: string,
    actor: ServiceCatalogActor,
    fields?: CatalogItemFields,
    source?: PMPartsAndProcedure | null,
): Promise<PMPartsAndProcedure | null> {
    const partNumber = catalogPartNumberValue(fields?.partNumber) || catalogPartNumberValue(source?.partNumber);
    const manufacturer = catalogTextValue(fields?.manufacturer) || catalogTextValue(source?.manufacturer);
    const unitOfMeasure = catalogTextValue(fields?.unitOfMeasure) || catalogTextValue(source?.unitOfMeasure);
    const vendorFields = vendorWriteFields(fields) || (catalogHasVendor(source) ? vendorWriteFields(source) : null);

    const created = await createServiceItem({
        companyId: actor.companyId,
        itemName,
        category: categoryName,
        itemType: source?.itemType || 'Consumable',
        unitOfMeasure: unitOfMeasure || null,
        createdByUserId: actor.createdByUserId,
        createdByUserName: actor.createdByUserName,
        ...(partNumber ? { partNumber } : {}),
        ...(manufacturer ? { manufacturer } : {}),
        ...(vendorFields || {}),
    });
    return asCatalogItem({
        ...created,
        itemName,
        category: categoryName,
        partNumber: partNumber || (created as PMPartsAndProcedure | null)?.partNumber,
        manufacturer: manufacturer || (created as PMPartsAndProcedure | null)?.manufacturer,
        unitOfMeasure: unitOfMeasure || (created as PMPartsAndProcedure | null)?.unitOfMeasure,
        ...(vendorFields || {}),
    });
}

export async function resolveOrCreateCatalogListing(
    categoryName: string,
    itemName: string,
    items: PMPartsAndProcedure[],
    actor: ServiceCatalogActor,
    fields?: CatalogItemFields,
    sourceItem?: PMPartsAndProcedure | null,
): Promise<PMPartsAndProcedure | null> {
    const trimmedName = itemName.trim();
    if (!trimmedName) return sourceItem ?? null;

    const key: ListingKey = {
        itemName: trimmedName,
        partNumber: catalogPartNumberValue(fields?.partNumber) || sourceItem?.partNumber,
        manufacturer: catalogTextValue(fields?.manufacturer) || sourceItem?.manufacturer,
        vendorId: fields?.vendorId ?? sourceItem?.vendorId,
        vendorName: fields?.vendorName ?? sourceItem?.vendorName,
    };

    const exact = findMatchingListing(items, categoryName, key);
    if (exact) {
        const patch = listingWritePatch(exact, fields);
        if (Object.keys(patch).length === 0) return exact;
        const updated = await updateServiceItem({ id: exact.id, ...patch });
        return asCatalogItem({ ...exact, ...updated, ...patch });
    }

    const hasBoundSource = Boolean(sourceItem && !isServiceItemPlaceholder(sourceItem));
    if (hasBoundSource && canFillListingInPlace(sourceItem!, fields)) {
        const patch = listingWritePatch(sourceItem!, { ...sourceItem, ...fields });
        if (Object.keys(patch).length === 0) return sourceItem!;
        const updated = await updateServiceItem({ id: sourceItem!.id, ...patch });
        return asCatalogItem({ ...sourceItem, ...updated, ...patch });
    }

    const sameName = itemsInCategory(items, categoryName).filter(
        (item) => item.itemName.trim().toLowerCase() === trimmedName.toLowerCase(),
    );

    // Unbound typed names may fill the only matching listing. A bound listing that
    // cannot accept the new vendor/manufacturer/part becomes a sibling instead of
    // overwriting a random same-name row.
    if (!hasBoundSource && sameName.length === 1 && canFillListingInPlace(sameName[0], fields)) {
        const fillable = sameName[0];
        const patch = listingWritePatch(fillable, fields);
        if (Object.keys(patch).length === 0) return fillable;
        const updated = await updateServiceItem({ id: fillable.id, ...patch });
        return asCatalogItem({ ...fillable, ...updated, ...patch });
    }

    const stub = findCategoryStub(items, categoryName);
    if (!hasBoundSource && stub && sameName.length === 0) {
        const partNumber = catalogPartNumberValue(fields?.partNumber);
        const manufacturer = catalogTextValue(fields?.manufacturer);
        const unitOfMeasure = catalogTextValue(fields?.unitOfMeasure);
        const vendorFields = vendorWriteFields(fields);
        const updated = await updateServiceItem({
            id: stub.id,
            itemName: trimmedName,
            ...(partNumber ? { partNumber } : {}),
            ...(manufacturer ? { manufacturer } : {}),
            ...(unitOfMeasure ? { unitOfMeasure } : {}),
            ...(vendorFields || {}),
        });
        return asCatalogItem({
            ...stub,
            ...updated,
            itemName: trimmedName,
            partNumber: partNumber || stub.partNumber,
            manufacturer: manufacturer || stub.manufacturer,
            unitOfMeasure: unitOfMeasure || stub.unitOfMeasure,
            ...(vendorFields || {}),
        });
    }

    return createListingRow(categoryName, trimmedName, actor, fields, sourceItem);
}

export async function createOrFillCatalogItem(
    categoryName: string,
    itemName: string,
    items: PMPartsAndProcedure[],
    actor: ServiceCatalogActor,
    fields?: CatalogItemFields,
): Promise<PMPartsAndProcedure | null> {
    return resolveOrCreateCatalogListing(categoryName, itemName, items, actor, fields);
}

export async function updateCatalogItemPartNumber(
    itemId: string,
    partNumber: string,
    existing?: PMPartsAndProcedure,
): Promise<PMPartsAndProcedure | null> {
    const nextPartNumber = catalogPartNumberValue(partNumber);
    if ((existing?.partNumber || '').trim() === (nextPartNumber || '')) {
        return existing ?? null;
    }
    const updated = await updateServiceItem({ id: itemId, partNumber: nextPartNumber });
    return asCatalogItem({ ...existing, ...updated, id: itemId, partNumber: nextPartNumber });
}

export async function updateCatalogItemVendor(
    itemId: string,
    vendor: Pick<CatalogItemFields, 'vendorId' | 'vendorName'>,
    existing?: PMPartsAndProcedure,
): Promise<PMPartsAndProcedure | null> {
    if (catalogHasVendor(existing)) return existing ?? null;
    const vendorFields = vendorWriteFields(vendor);
    if (!vendorFields) return existing ?? null;
    const updated = await updateServiceItem({ id: itemId, ...vendorFields });
    return asCatalogItem({ ...existing, ...updated, id: itemId, ...vendorFields });
}

export function catalogFieldsForPrompt(
    item: PMPartsAndProcedure,
    vendors: CatalogVendor[] = [],
    currentPrompt?: Pick<tPrompt, 'vendor' | 'partNumber' | 'partDescription' | 'defaultUM'> | null,
): Partial<tPrompt> {
    return {
        serviceItemId: item.id,
        component: item.itemName.trim(),
        partNumber: item.partNumber || currentPrompt?.partNumber || '',
        vendor: vendorLabelForCatalogItem(item, vendors) || currentPrompt?.vendor || '',
        partDescription: item.manufacturer || currentPrompt?.partDescription || '',
        defaultUM: item.unitOfMeasure || currentPrompt?.defaultUM || '',
    };
}
